import {
  afterNextRender,
  Component,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  TransferState,
  makeStateKey,
  output,
  PLATFORM_ID,
  signal,
  TemplateRef,
} from '@angular/core';
import { DataService } from '@app/services/data.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import {
  DOCUMENT,
  isPlatformBrowser,
  isPlatformServer,
  KeyValuePipe,
} from '@angular/common';
import { deserializeViewParameters, getViewParameters } from '@app/helpers/view';
import { ViewFiltersComponent } from './view-filters/view-filters.component';
import { DrupalService } from '@app/services/drupal.service';
import { ViewResultsComponent } from './view-results/view-results.component';
import { ViewRow, ViewResponse } from '@app/models/view.model';

@Component({
  selector: 'mobi-view',
  standalone: true,
  imports: [ViewFiltersComponent, ViewResultsComponent, KeyValuePipe],
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
})
export class ViewComponent implements OnInit, OnDestroy {
  readonly results = input.required<TemplateRef<any>>();
  readonly scrollable = input(false);
  readonly viewId = input('');
  readonly deepLink = input(false);
  readonly centerFilters = input(false);
  readonly title = input('');
  readonly preventEmptyResultsFilter = input('');
  readonly preventEmptyResults = input(false);
  readonly autofocus = input(false);
  readonly isOverview = input(true);
  readonly rowFormatter = input<(...args: any) => any>();
  readonly includes = input<string[]>([]); // array of field ids to resolve if they are referenced entities
  readonly display = input<string>('default'); // view mode for views with different modes
  readonly groupFilter = input<string[]>([]);
  readonly hideFilters = input<string[]>([]);
  readonly apiBaseUrl = input<string>(''); // optional base URL for cross-platform API calls
  readonly argumentIndexMap = input<{ [key: string]: number }>({}); // maps field-name keys to Drupal view positional indices
  readonly filters = model<{ [key: string]: any }>({});
  readonly argument = model<{ [key: string]: any }>({});

  readonly filterChange = output<{ [key: string]: any }>();
  readonly emptyView = output<boolean>();

  rows = signal<ViewRow[]>([]);
  currentPage = signal(0);
  view = signal<ViewResponse | null>(null);
  loading = signal(true);
  loadingMore = signal(false);
  exposedFilters = signal<any[]>([]);

  #viewResult$: Subject<ViewResponse> = new Subject<ViewResponse>();

  #unformattedRows: any[] = [];
  #debouncedSubject: Subject<any> = new Subject();
  #textFilterDebounceMs: number = 500;
  #pageSub!: Subscription;
  #debounceSub!: Subscription;
  #filterKey: string = '';
  #prevFilterUrl: string = '';
  #queryGeneration: number = 0;

  /**
   * Resolves argument keys using argumentIndexMap.
   * Field-name keys are converted to numeric indices for Drupal views contextual filters.
   * Keys not in the map pass through unchanged.
   */
  #resolvedArgument(): { [key: string]: any } {
    const map = this.argumentIndexMap();
    if (!Object.keys(map).length) return this.argument();
    const resolved: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(this.argument())) {
      const resolvedKey = key in map ? map[key].toString() : key;
      resolved[resolvedKey] = value;
    }
    return resolved;
  }

  #platformId = inject(PLATFORM_ID);
  #doc = inject(DOCUMENT);
  #data = inject(DataService);
  #drupal = inject(DrupalService);
  #transferState = inject(TransferState);

  #afterRender = afterNextRender({
    write: () => {
      if (!this.autofocus()) return;
      const searchfield: HTMLInputElement | null =
        this.#doc.querySelector('.searchfield input');
      if (searchfield) searchfield.focus();
    },
  });

  get #viewResultObs() {
    return this.#viewResult$;
  }

  #startNewQuery(): number {
    this.#queryGeneration += 1;
    return this.#queryGeneration;
  }

  #initialViewKey(page: number) {
    return makeStateKey<ViewResponse>(
      `mobi.initial-view.${JSON.stringify({
        path: this.#doc.location.pathname,
        id: this.viewId(),
        page,
        display: this.display(),
        filters: this.filters(),
        argument: this.#resolvedArgument(),
        includes: this.includes(),
        apiBaseUrl: this.apiBaseUrl(),
      })}`
    );
  }

  #requestViewPage(page: number, queryGeneration: number): Promise<void> {
    return this.#data
      .loadView(
        this.viewId(),
        page,
        this.filters(),
        this.#resolvedArgument(),
        this.includes(),
        this.display(),
        this.apiBaseUrl()
      )
      .then((result) => {
        // Ignore stale responses that belong to a previous filter/query generation.
        if (queryGeneration !== this.#queryGeneration) return;
        const transferKey =
          isPlatformServer(this.#platformId) && Array.isArray(result?.data)
            ? this.#initialViewKey(page)
            : null;
        this.#viewResult$.next(result);
        if (transferKey && queryGeneration === this.#queryGeneration) {
          this.#transferState.set(transferKey, result);
        }
      })
      .catch((e) => {
        if (queryGeneration !== this.#queryGeneration) return;
        this.loading.set(false);
        this.loadingMore.set(false);
        this.#handleError(e);
      });
  }

  ngOnInit(): void {
    this.#pageSub = this.#viewResultObs
      // .pipe(distinctUntilChanged())
      .subscribe((response: ViewResponse) => {
        this.view.set(response);
        if (this.view()?.meta?.exposed_filters) {
          this.getExposedFilters(this.view()!.meta.exposed_filters);
        }

        // abort if no results are found and preventEmptyResults is set
        // also abort if the same filter is applied again
        if (
          (!this.view()?.data?.length && !this.preventEmptyResults()) ||
          (this.preventEmptyResults() && this.#prevFilterUrl === this.#doc.location.href)
        ) {
          if (this.currentPage() === 0) {
            this.rows.set([]);
          }
          this.loading.set(false);
          this.loadingMore.set(false);
          this.emptyView.emit(this.rows().length === 0);
          return;
        }
        if (this.preventEmptyResults()) this.#prevFilterUrl = this.#doc.location.href;

        if (this.view()?.data) {
          if (this.rowFormatter()) {
            this.#unformattedRows = [...this.#unformattedRows, ...this.view()!.data];
            this.rows.set(this.rowFormatter()!(this.#unformattedRows));
          } else {
            this.rows.update((rows) => [...rows, ...this.view()!.data]);
          }
        }

        // if preventEmptyResults is set, the preventEnptyResultsFilter is not selected and results are found, set the first option of the filter
        if (
          this.preventEmptyResults() &&
          this.rows.length &&
          !this.filters()[this.preventEmptyResultsFilter()]
        ) {
          this.exposedFilters().forEach((filter) => {
            if (filter['#name'] === this.preventEmptyResultsFilter()) {
              let key = filter['#optionsArray'][0].key;
              if (key === 'All') key = filter['#optionsArray'][1].key;

              this.filterView(this.preventEmptyResultsFilter(), key);
            }
          });
        }

        // if preventEmptyResults is set and no results are found, filter the view without a value for the preventEmptyResultsFilter
        if (this.preventEmptyResults() && !this.rows().length) {
          this.filterView(this.preventEmptyResultsFilter(), null);
        } else {
          this.loading.set(false);
          this.loadingMore.set(false);
          this.emptyView.emit(this.rows().length === 0 && !this.loading());

          // if combine filter has triggered view loading set focus on combine filter
          if (
            isPlatformServer(this.#platformId) ||
            !this.#filterKey ||
            this.view()?.meta?.exposed_filters[this.#filterKey]['#type'] !== 'textfield'
          ) {
            return;
          }
        }
      });

    if (this.deepLink()) {
      const query = `?${this.#drupal.queryParams().toString()}`;

      const deserializedParams: any = deserializeViewParameters(query);

      if (deserializedParams.page) {
        this.currentPage.set(deserializedParams.page);
      }
      if (Object.keys(deserializedParams.filter).length) {
        this.filters.set(deserializedParams.filter);
      }

      if (Object.keys(deserializedParams.argument).length) {
        this.argument.set(deserializedParams.argument);
      }
    }

    const initialQueryGeneration = this.#startNewQuery();
    for (let page = 0; page <= this.currentPage(); page++) {
      if (isPlatformBrowser(this.#platformId)) {
        const key = this.#initialViewKey(page);
        const initialView = this.#transferState.get(key, null);
        if (initialView) {
          this.#transferState.remove(key);
          this.#viewResult$.next(initialView);
          if (initialQueryGeneration !== this.#queryGeneration) break;
          continue;
        }
      }
      this.#requestViewPage(page, initialQueryGeneration);
    }

    this.#debounceSub = this.#debouncedSubject
      .pipe(debounceTime(this.#textFilterDebounceMs))
      .subscribe(([key, value]) => this.filterView(key, value));
  }

  ngOnDestroy(): void {
    this.#queryGeneration += 1;
    if (this.#pageSub) this.#pageSub.unsubscribe();
    if (this.#debounceSub) this.#debounceSub.unsubscribe();
    this.resetView();
    this.filters.set({});
    this.#filterKey = '';
  }

  resetView(): void {
    this.view.set(null);
    this.rows.set([]);
    this.currentPage.set(0);
    this.#unformattedRows = [];
    this.loading.set(false);
    this.loadingMore.set(false);
  }

  loadNext(): void {
    this.loadingMore.set(true);
    this.currentPage.update((page) => page + 1);
    this.updateUrl();
    this.#requestViewPage(this.currentPage(), this.#queryGeneration).finally(() => {
      this.loadingMore.set(false);
    });
  }
  /**
   * Handle filter updates from the filters component
   */
  onFilterUpdate(event: { key: string; value: any; immediate?: boolean }) {
    const { key, value, immediate = false } = event;

    if (value === null) {
      this.filterView(key, null);
      return;
    }

    if (immediate) {
      this.filterView(key, value);
    } else {
      this.#debouncedSubject.next([key, value]);
    }
  }

  /**
   * Handle selection updates from the filters component
   */
  updateSelection(key: string, values: any) {
    let selection: any[] = this.filters()[key];

    if (!Array.isArray(values)) values = [values];
    if (!selection) selection = [];

    values.forEach((value: any) => {
      if (selection.includes(value)) {
        const index: number = selection.indexOf(value);
        selection.splice(index, 1);
      } else {
        selection.push(value);
      }
    });

    this.filterView(key, selection);
  }

  /**
   * Filter the view and update all variables
   *
   * @param key filter id used as key in the filters array
   * @param value value of the filter
   */
  filterView(key: string, value: any) {
    this.#filterKey = key;
    const filters = { ...this.filters() };

    if (value === null) {
      delete filters[key];
    } else {
      filters[key] = value;
    }

    this.filters.set(filters);

    this.resetView();
    this.loading.set(true);
    const queryGeneration = this.#startNewQuery();

    this.updateUrl();
    this.#requestViewPage(this.currentPage(), queryGeneration);
    this.filterChange.emit(this.filters());
  }

  updateUrl() {
    if (isPlatformBrowser(this.#platformId) && this.deepLink()) {
      const url: string =
        window.location.origin +
        window.location.pathname +
        getViewParameters(this.currentPage(), this.filters());
      if (isPlatformBrowser(this.#platformId)) {
        window.history.replaceState({}, '', url);
      }
    }
  }

  get hasPagination(): boolean {
    return !!this.view()?.links?.next?.href;
  }

  generateNumberOptions(filter: any): { key: string; value: string }[] {
    if (!filter['#attributes'].min || !filter['#attributes'].max) return [];
    const options: { key: string; value: string }[] = [];

    if (
      filter['#attributes']?.min !== undefined &&
      filter['#attributes']?.max !== undefined
    ) {
      const min = Number(filter['#attributes'].min);
      const max = Number(filter['#attributes'].max);

      for (let i = min; i <= max; i++) {
        options.push({ key: i.toString(), value: i.toString() });
      }
    }

    return options;
  }

  getExposedFilters(filters: any[]) {
    const exposedFilters: any[] = [];
    Object.keys(filters)?.forEach((key: any) => {
      let include = [];
      const filter = filters[key];
      const isSelect = filters[key]['#type'] === 'select';

      // make sure optionsArray has processed labels for the options
      // as the drupal count processing happens after the optionsArray is set
      if (filter['#optionsArray'] && filter['#options']) {
        filter['#formattedOptions'] = filter['#optionsArray'].map((option: any) => {
          option.value = filter['#options'][option.key] || option.value;
          return option;
        });
      }

      if (filter['#type'] === 'number' && !filter['#optionsArray']) {
        filter['#optionsArray'] = this.generateNumberOptions(filter);
      }

      // Define include array to filter options based on view arguments
      if (Object.keys(this.argument()).includes(key)) {
        const arg = this.argument()[key] || '';
        if (arg.includes('+')) include = arg.split('+');
        if (arg.includes(',')) include = arg.split(',');
        if (arg.length > 0 && !arg.includes('+') && !arg.includes(',')) {
          include = [arg];
        }
      }

      // If filter is defined as argument but has no restriction
      // disable completely if not in overview mode
      if (!this.isOverview() && this.argument()[key] === null && !include?.length) {
        return;
      }

      // Get options from the filter object (needs to be array of objects)
      // Relies on logic on line 199 of  backend/web/modules/custom/jsonapi_views/src/Resource/ViewsResource.php
      let options: { key: string; value: string; children?: any[] }[] =
        filter['#optionsArray'] || [];

      const childOptions: { [key: string]: any[] } = {};
      if (filter['#nested'] || filter['#bef_nested'] || isSelect) {
        // if the filter is nested, filter out the child elements
        // and save the mapping in an temporary array
        let currentParent!: string;
        options = options.filter((option) => {
          const value = option.value;

          if (!value.startsWith('-')) {
            currentParent = option.key;
            return true;
          }

          option.value = value.replace('-', '');

          if (!childOptions[currentParent]) childOptions[currentParent] = [];
          childOptions[currentParent].push(option);
          return false;
        });

        // Map the child options to the parent options and
        // filter out the child options based on the include array
        options = options.map((option) => {
          if (!Object.keys(childOptions).includes(option.key)) return option;

          if (!this.isOverview()) {
            childOptions[option.key] = childOptions[option.key].filter((childElem) => {
              return include.length === 0 || include.includes(`${childElem.key}`);
            });
          }

          // if no includes are definned (generally without arguments) or
          // if parent is activated based on include array and has more than 1 child
          // add parent option as first, hidden child in order to include it into the
          // select all action
          if (
            include?.length === 0 ||
            (include.includes(`${option.key}`) && childOptions[option.key]?.length > 1)
          ) {
            childOptions[option.key] = [
              {
                ...option,
                hidden: true,
              },
              ...childOptions[option.key],
            ];
          }

          return {
            ...option,
            children: childOptions[option.key],
          };
        });
      }

      // Filter out the options based on the include array
      // make sure to also check the children if defined
      // make sure that parent option is present if it
      // has child includes (even if parent is not included)
      if (!this.isOverview()) {
        options = options.filter((elem) => {
          let childIncludes = false;

          childOptions[elem.key]?.forEach((childElem) => {
            childIncludes = childIncludes || include.includes(`${childElem.key}`);
          });

          return include.length === 0 || include.includes(`${elem.key}`) || childIncludes;
        });
      }
      // if the filter is a select, wrap the options in a parent option
      if (isSelect) {
        options = [
          {
            key: '0',
            value: filter['#title'],
            children: options,
          },
        ];
      }

      filter['#processedOptions'] = options;

      exposedFilters.push(filter);
    });

    this.exposedFilters.set(exposedFilters);
  }

  #handleError(e: any) {
    console.error(e);
    this.#doc?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }
}
